import { Suspense } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/catalyst/button";
import { PlusIcon, BriefcaseIcon } from "@heroicons/react/24/outline";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { CreateWorkOrderJobOpeningDialog } from "@/app/ProDrivers/WorkOrders/Components/Dialogs/CreateWorkOrderJobOpeningDialog";
import { WorkOrderJobOpeningCard } from "@/app/ProDrivers/WorkOrders/Components/Cards/WorkOrderJobOpeningCard";
import { useWorkOrderRecord } from "@/app/ProDrivers/WorkOrders/Hooks/useWorkOrderRecord";
import { useSuspenseGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { workOrderJobOpeningListQuery } from "@/app/ProDrivers/WorkOrders/GraphQL/workOrderJobOpeningQuery";
import { useTranslations } from "use-intl";
import {
  QueryWorkOrderJobOpeningsOrderByColumn,
  SortOrder,
} from "@/gql/graphql";
import { Skeleton } from "@/components/ui/skeleton";

export function WorkOrderJobOpenings() {
  const t = useTranslations("workOrder.jobOpenings");
  const workOrder = useWorkOrderRecord();
  const { open } = useDialog(CreateWorkOrderJobOpeningDialog);

  const handleCreateJobOpening = () => {
    open({
      workOrderId: workOrder.id,
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">{t("title")}</h3>
        <Button onClick={handleCreateJobOpening}>
          <PlusIcon className="h-4 w-4" />
          {t("createJobOpening")}
        </Button>
      </div>

      <Suspense fallback={<Skeleton className="h-48 w-full rounded-lg" />}>
        <WorkOrderJobOpeningsList
          workOrderId={workOrder.id}
          onCreateJobOpening={handleCreateJobOpening}
        />
      </Suspense>
    </div>
  );
}

function WorkOrderJobOpeningsList({
  workOrderId,
  onCreateJobOpening,
}: {
  workOrderId: string;
  onCreateJobOpening: () => void;
}) {
  const t = useTranslations("workOrder.jobOpenings");

  const { data } = useSuspenseGqlQuery(workOrderJobOpeningListQuery, {
    work_order_id: workOrderId,
    first: 20,
    orderBy: [
      {
        column: QueryWorkOrderJobOpeningsOrderByColumn.CreatedAt,
        order: SortOrder.Asc,
      },
    ],
  });

  const jobOpenings = data.workOrderJobOpenings.data;

  if (jobOpenings.length === 0) {
    return (
      <Card className="cursor-pointer border-2 border-dashed border-gray-300 transition-colors hover:border-gray-400 hover:bg-gray-50">
        <CardContent
          className="flex flex-col items-center justify-center py-8"
          onClick={onCreateJobOpening}
        >
          <div className="flex size-12 items-center justify-center rounded-full bg-gray-100">
            <BriefcaseIcon className="size-6 text-gray-400" />
          </div>
          <h3 className="mt-3 text-sm font-medium text-gray-900">
            {t("noJobOpenings")}
          </h3>
          <p className="mt-1 text-xs text-gray-500">{t("createToNotify")}</p>
          <Button outline className="pointer-events-none mt-4">
            <PlusIcon className="h-4 w-4" />
            {t("createJobOpening")}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {jobOpenings.map((jobOpening, index) => (
        <WorkOrderJobOpeningCard
          key={jobOpening.id}
          jobOpening={jobOpening}
          jobNumber={index + 1}
        />
      ))}
    </div>
  );
}
